import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowSquareOutIcon,
  CaretDownIcon,
  CaretUpIcon,
  EyeIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react'
import { mappedColors, typography } from '@pixee/design-system'
import { GitBranchIcon, GitMergeIcon } from '@primer/octicons-react'
import * as Accordion from '@radix-ui/react-accordion'
import * as Dialog from '@radix-ui/react-dialog'
import * as Tabs from '@radix-ui/react-tabs'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import { match, P } from 'ts-pattern'
import { useNewerScanBanner } from '../../hooks/use-newer-scan-banner.ts'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service'
import {
  useGetAnalysisPreferences,
  useGetFixResultChangesets,
  useGetFixResultPatches,
  useGetRepositoryFromHalLink,
  useGetResultActivitySSE,
  useGetScan,
  useGetScanAnalysis,
  useGetScanFinding,
  useGetScanFindingResults,
  useIsPostScanPatchMutating,
  usePostScanPatch,
} from '../../utils/api-client/api-hooks.ts'
import {
  RepositoryType,
  type BlockedFixOutcome,
  type Changeset,
  type CompletedFixOutcome,
  type Confidence,
  type ExcludedFixOutcome,
  type FailedFixOutcome,
  type FindingResponseWithoutEmbed,
  type FindingResultPaginatedResponse,
  type FixResultResponse,
  type MergeStateResponse,
  type NoRecommendationsFixOutcome,
  type QualityResponse,
} from '../../utils/api-client/api-schemas.ts'
import { shouldShowFixConfetti } from '../../utils/confirmation-utils.ts'
import {
  getAnalysisMetadata,
  getFirstCompletedScaResultFromResults,
  getFirstCompletedTriageResultFromResults,
  getFirstFixResultFromResults,
  getResultById,
} from '../../utils/finding-utils'
import { pluralize } from '../../utils/text-utils'
import { AnalysisActivityTab, type AnalysisActivityTabProps } from '../activity-feed/analysis-activity-tab.tsx'
import { AnalysisTransitionOverlay } from '../activity-feed/analysis-transition-overlay.tsx'
import { OutcomeBanner } from '../activity-feed/outcome-banner.tsx'
import { StatusNotification, type StatusNotificationStatus } from '../activity-feed/status-notification.tsx'
import { useActivityResultSelection } from '../activity-feed/use-activity-result-selection.ts'
import { useActivityTabBehavior } from '../activity-feed/use-activity-tab-behavior.ts'
import { ScanStatusBadge } from '../badges/scan-status-badge.tsx'
import { CopyButton } from '../buttons/copy-button.tsx'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import * as alertDialogStyles from '../delete-repository-dialog.css.ts'
import { DrawerConfetti } from '../drawer-confetti.tsx'
import { ErrorBoundary } from '../error-boundaries/error-boundary'
import { OutcomeReasonDetails } from '../outcomes/outcome-reason-details.tsx'
import { PulsingDot } from '../pulsing-dot.tsx'
import { RenderedMarkdown } from '../rendered-markdown.tsx'
import * as scrollableTabBarStyles from '../scrollable-tab-bar/scrollable-tab-bar.css.ts'
import { ScrollableTabBar } from '../scrollable-tab-bar/scrollable-tab-bar.tsx'
import { Tooltip, TooltipWithHover } from '../tooltip.tsx'
import { AnalysisPreferencesTab } from './analysis-preferences-tab.tsx'
import { ChangesetDiffView } from './changeset-diff-view.tsx'
import { useDrawerContentDimensions } from './drawer-utils.ts'
import { FeedbackBar } from './feedback-bar.tsx'
import * as styles from './fix-finding-drawer.css.ts'
import * as footerStyles from './floating-footer.css'
import { ResultNotFoundFallback } from './result-not-found-fallback.tsx'
import { useNavigateScanFindingDrawers } from './use-navigate-scan-finding-drawers.ts'

type NonCompletedFixOutcome = FailedFixOutcome | BlockedFixOutcome | ExcludedFixOutcome | NoRecommendationsFixOutcome

export const STARTER_FIX_THRESHOLD = 80

type FixFindingDrawerTab = 'overview' | 'files-changed' | 'details' | 'analysis-activity' | 'preferences'

export type FixFindingDrawerProps = {
  scanId: string
}

type DrawerStateMessageProps = {
  srOnlyDescription: string
  children: React.ReactNode
}

const DrawerStateMessage: React.FC<DrawerStateMessageProps> = ({ srOnlyDescription, children }) => (
  <>
    <VisuallyHidden>
      <Dialog.Title>Finding Details</Dialog.Title>
      <Dialog.Description>{srOnlyDescription}</Dialog.Description>
    </VisuallyHidden>
    {children}
  </>
)

export const FixFindingDrawer: React.FC<FixFindingDrawerProps> = ({ scanId }) => {
  const { findingFixFindingId, resultId, onClose, onOpenFindingTriageDrawer, onNavigateToResult } =
    useNavigateScanFindingDrawers({
      scanId,
    })

  const findingId = findingFixFindingId
  const isOpen = !!findingId

  const { data: scan } = useGetScan({ id: scanId })

  const { data: repository } = useGetRepositoryFromHalLink({
    path: scan?._links?.repository?.href,
  })
  const repositoryType = repository?.type ?? undefined

  const {
    data: finding,
    isLoading: isFindingLoading,
    isError: isFindingError,
    isSuccess: isFindingSuccess,
  } = useGetScanFinding({ scanId, findingId })

  const { data: results } = useGetScanFindingResults({
    scanId,
    findingId,
    representative: !resultId,
    refetchInterval: findingId ? 1000 : undefined,
  })

  // Representative results exclude in-progress results, so fetch all results
  // separately for live detection during re-analysis.
  const { data: allResults } = useGetScanFindingResults({
    scanId,
    findingId,
    representative: false,
    enabled: !resultId,
    refetchInterval: findingId ? 1000 : undefined,
  })

  const completedFixResult: FixResultResponse | undefined = resultId
    ? getResultById({ results, resultId, type: 'fix' })
    : getFirstFixResultFromResults(results)

  const completedTriageResult = getFirstCompletedTriageResultFromResults(results)
  const completedScaResult = getFirstCompletedScaResultFromResults(results)

  const relatedAnalysisResultId = findRelatedAnalysisResultId(results, completedFixResult, resultId)

  const isResultNotFound = !!resultId && isFindingSuccess && !completedFixResult

  const { outcome, completedFixOutcome, nonCompletedOutcome, isInProgress, isLive } =
    classifyFixOutcome(completedFixResult)

  const {
    activityProps,
    isActivityTabVisible,
    selectedTab,
    handleTabChange,
    notification,
    onNotificationAction,
    showConfirmation,
    showConfetti,
    onConfettiComplete,
    analysis,
  } = useFixActivityTabBehavior({
    finding,
    results,
    allResults,
    resultId,
    completedFixResult,
    isLive,
    isInProgress,
    outcome,
    completedFixOutcome,
    onNavigateToResult,
  })

  const newerScan = useNewerScanBanner({
    currentScanId: scanId,
    repositoryId: repository?.id,
    tool: scan?.detector,
  })
  const isStale = newerScan?.state === 'has-analysis'
  const currentScanImportedAt = scan?.imported_at ?? undefined
  const newerScanImportedAt = newerScan?.newerScanImportedAt

  const { drawerContentRef, contentDimensions } = useDrawerContentDimensions({ isEnabled: !!finding })

  const { handleSendPatch, staleConfirmDialog } = useSendPatch({
    scanId,
    completedFixResultId: completedFixResult?.id,
    isStale,
    currentScanImportedAt,
    newerScanImportedAt,
    repositoryType,
    onClose,
  })

  const hasFeedbackBar = !!completedFixResult?.id && !isLive && !isInProgress && !showConfirmation
  const hasFloatingFooter = hasFeedbackBar

  if (!isOpen) {
    return null
  }

  return (
    <Dialog.Root open={true} modal={false}>
      <Dialog.Content
        ref={drawerContentRef}
        className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
        data-has-floating-footer={hasFloatingFooter ? true : undefined}
        onEscapeKeyDown={onClose}
        onPointerDownOutside={event => event.preventDefault()}
        onInteractOutside={event => event.preventDefault()}
      >
        <Dialog.Close asChild className={styles.closeButton}>
          <button aria-label="Close" onClick={onClose}>
            <XIcon className={styles.closeButtonIcon} size={24} weight="light" />
          </button>
        </Dialog.Close>
        <ErrorBoundary fallbackTitle="Failed to display fix details">
          {match({ isFindingLoading, isFindingError, isResultNotFound, finding })
            .with({ isFindingLoading: true }, () => (
              <DrawerStateMessage srOnlyDescription="Loading finding details">
                <div className={styles.stateMessage}>
                  <p className={typography.paragraph}>Loading finding details...</p>
                </div>
              </DrawerStateMessage>
            ))
            .with({ isFindingError: true }, () => (
              <DrawerStateMessage srOnlyDescription="Error loading finding details">
                <div className={styles.stateMessage}>
                  <p className={typography.paragraph}>Failed to load finding details.</p>
                </div>
              </DrawerStateMessage>
            ))
            .with({ isResultNotFound: true }, () => (
              <DrawerStateMessage srOnlyDescription="Result not found">
                <ResultNotFoundFallback />
              </DrawerStateMessage>
            ))
            .with({ finding: P.not(P.nullish) }, ({ finding }) => {
              const hasTriageResult = !!completedTriageResult || !!completedScaResult
              const onViewTriageResult = () => onOpenFindingTriageDrawer(finding.id, relatedAnalysisResultId)

              return (
                <>
                  <FixPreviewHeader
                    findingTitle={finding.title}
                    findingId={finding.id}
                    scanId={scanId}
                    completedFixOutcome={completedFixOutcome}
                    nonCompletedOutcome={nonCompletedOutcome}
                    completedFixResultId={completedFixResult?.id}
                    repositoryType={repositoryType}
                    onSendPatch={handleSendPatch}
                    isStale={isStale}
                  />
                  {match({ nonCompletedOutcome, isInProgress })
                    .with({ nonCompletedOutcome: P.not(P.nullish) }, ({ nonCompletedOutcome }) => (
                      <NonCompletedOutcomeSummary outcome={nonCompletedOutcome} />
                    ))
                    .with({ isInProgress: true }, () => (
                      <InProgressActivityView
                        hasTriageResult={hasTriageResult}
                        onBackToTriageResult={onViewTriageResult}
                        activityProps={activityProps}
                        isLive={isLive}
                        showConfirmation={showConfirmation}
                      />
                    ))
                    .otherwise(() => (
                      <CompletedFixResultTabs
                        selectedTab={selectedTab}
                        onTabChange={handleTabChange}
                        completedFixOutcome={completedFixOutcome}
                        completedFixResultId={completedFixResult?.id}
                        notification={notification}
                        onNotificationAction={onNotificationAction}
                        activityProps={activityProps}
                        isActivityTabVisible={isActivityTabVisible}
                        isLive={isLive}
                        showConfirmation={showConfirmation}
                        analysisId={analysis?.id}
                        hasTriageResult={hasTriageResult}
                        onViewTriageResult={onViewTriageResult}
                      />
                    ))}
                </>
              )
            })
            .otherwise(() => (
              <DrawerStateMessage srOnlyDescription="No finding data available">
                <div className={styles.stateMessage}>
                  <p className={typography.paragraph}>No finding data available.</p>
                </div>
              </DrawerStateMessage>
            ))}
          {showConfetti && contentDimensions.width > 0 && (
            <DrawerConfetti
              width={contentDimensions.width}
              height={contentDimensions.height}
              onComplete={onConfettiComplete}
            />
          )}
        </ErrorBoundary>
      </Dialog.Content>
      {hasFeedbackBar && (
        <FeedbackBar
          resultId={completedFixResult.id}
          resultType="fix"
          scanId={scanId}
          drawerContentRef={drawerContentRef}
        />
      )}
      {staleConfirmDialog}
    </Dialog.Root>
  )
}

const useSendPatch = ({
  scanId,
  completedFixResultId,
  isStale,
  currentScanImportedAt,
  newerScanImportedAt,
  repositoryType,
  onClose,
}: {
  scanId: string
  completedFixResultId: string | undefined
  isStale: boolean | undefined
  currentScanImportedAt: string | undefined
  newerScanImportedAt: string | undefined
  repositoryType: RepositoryType | undefined
  onClose: () => void
}) => {
  const { addToast } = useToastService()
  const showMutationError = useShowMutationError({ prefix: 'Failed to send patch' })
  const { mutate: postScanPatch } = usePostScanPatch({
    scanId,
    onSuccess: () => {
      addToast({ message: <>1 patch triggered successfully</>, variant: 'success' })
      onClose()
    },
    onError: showMutationError,
  })

  const onSendPatch = useCallback(() => {
    invariant(scanId, 'scanId is required')
    invariant(completedFixResultId, 'completedFixResultId is required')
    postScanPatch({
      createScanPatchRequest: { fix_result_ids: [completedFixResultId] },
    })
  }, [scanId, completedFixResultId, postScanPatch])

  const [isStaleConfirmOpen, setIsStaleConfirmOpen] = useState(false)

  const handleSendPatch = useCallback(() => {
    if (isStale) {
      setIsStaleConfirmOpen(true)
    } else {
      onSendPatch()
    }
  }, [isStale, onSendPatch])

  const staleConfirmDialog = isStaleConfirmOpen ? (
    <StaleFixConfirmationDialogPure
      currentScanImportedAt={currentScanImportedAt}
      newerScanImportedAt={newerScanImportedAt}
      repositoryType={repositoryType}
      onCancel={() => setIsStaleConfirmOpen(false)}
      onConfirm={() => {
        setIsStaleConfirmOpen(false)
        onSendPatch()
      }}
    />
  ) : null

  return { handleSendPatch, staleConfirmDialog }
}

// When the drawer first observes results that include an in-progress fix, latch onto that
// detection so subsequent renders keep redirecting to and streaming activity for the in-progress
// result (rather than snapping back to a representative completed result).
const useLiveResultRedirect = ({
  results,
  allResults,
  resultId,
  activeResultId,
  hasInProgressResult,
  completedFixResultId,
  onNavigateToResult,
}: {
  results: FindingResultPaginatedResponse | undefined
  allResults: FindingResultPaginatedResponse | undefined
  resultId: string | undefined
  activeResultId: string | undefined
  hasInProgressResult: boolean
  completedFixResultId: string | undefined
  onNavigateToResult: ((resultId: string) => unknown) | undefined
}): string | undefined => {
  const hasProcessedInitialResultsRef = useRef(false)
  const openedWithLiveAnalysisRef = useRef(false)
  const liveDetectionResults = resultId ? results : allResults
  if (liveDetectionResults === undefined) {
    hasProcessedInitialResultsRef.current = false
    openedWithLiveAnalysisRef.current = false
  }
  if (!hasProcessedInitialResultsRef.current && liveDetectionResults !== undefined) {
    hasProcessedInitialResultsRef.current = true
    openedWithLiveAnalysisRef.current = hasInProgressResult
  }

  // Depend on all-results having loaded: `openedWithLiveAnalysisRef` is captured during the
  // render where all-results first loads, which need not change `activeResultId` (the
  // representative query can supply it first). Without this dep the effect runs once while
  // the ref is still false and never re-runs, leaving the drawer stuck in the tabbed view.
  const hasLoadedLiveDetectionResults = liveDetectionResults !== undefined
  useEffect(() => {
    if (openedWithLiveAnalysisRef.current && !resultId && activeResultId) {
      onNavigateToResult?.(activeResultId)
    }
  }, [resultId, activeResultId, onNavigateToResult, hasLoadedLiveDetectionResults])

  return openedWithLiveAnalysisRef.current && !resultId ? activeResultId : completedFixResultId
}

const useFixActivityTabBehavior = ({
  finding,
  results,
  allResults,
  resultId,
  completedFixResult,
  isLive,
  isInProgress,
  outcome,
  completedFixOutcome,
  onNavigateToResult,
}: {
  finding: FindingResponseWithoutEmbed | undefined
  results: FindingResultPaginatedResponse | undefined
  allResults: FindingResultPaginatedResponse | undefined
  resultId: string | undefined
  completedFixResult: FixResultResponse | undefined
  isLive: boolean
  isInProgress: boolean
  outcome: CompletedFixOutcome | NonCompletedFixOutcome | undefined
  completedFixOutcome: CompletedFixOutcome | undefined
  onNavigateToResult: ((resultId: string) => unknown) | undefined
}) => {
  const queryClient = useQueryClient()

  // Use all results for activity selection (falls back to representative while loading)
  const activityResults = resultId ? results : (allResults ?? results)
  const { activeResultId, isLive: hasInProgressResult } = useActivityResultSelection({
    results: activityResults,
    findingType: 'fix',
  })

  const sseResultId = useLiveResultRedirect({
    results,
    allResults,
    resultId,
    activeResultId,
    hasInProgressResult,
    completedFixResultId: completedFixResult?.id,
    onNavigateToResult,
  })

  const activityStream = useGetResultActivitySSE(sseResultId, 'fix')

  const analysisId = completedFixResult?._links.analysis.href?.split('/').pop()
  const { data: analysis } = useGetScanAnalysis({ analysisId })
  const analysisMetadata = useMemo(
    () => getAnalysisMetadata({ analysis, resultCompleted: completedFixResult?.completed }),
    [analysis, completedFixResult?.completed]
  )

  useEffect(() => {
    if (activityStream.connectionStatus === 'finished' && analysisId) {
      queryClient.invalidateQueries({ queryKey: ['scan-analysis', analysisId] })
    }
  }, [activityStream.connectionStatus, analysisId, queryClient])

  const activityProps: AnalysisActivityTabProps | undefined = activityStream.isEndpointAvailable
    ? {
        events: activityStream.events,
        connectionStatus: activityStream.connectionStatus,
        error: activityStream.error,
        metadata: analysisMetadata,
        isLive,
      }
    : undefined

  const isActivityTabVisible = !!activityProps && outcome?.type !== 'blocked-fix' && outcome?.type !== 'excluded-fix'

  const {
    selectedTab,
    handleTabChange,
    notification: hookNotification,
    switchToActivityTab,
    showConfirmation,
    showConfetti,
    onConfettiComplete,
  } = useActivityTabBehavior<FixFindingDrawerTab>({
    isLive,
    connectionStatus: activityProps?.connectionStatus,
    // Default to 'overview'; the useEffect below switches to the activity tab once
    // queries resolve and isInProgress is known. Defaulting to 'analysis-activity' here
    // would cause the wrapper's first render (before queries resolve) to lock selectedTab
    // onto the activity tab even when the fetched result turns out to be completed.
    defaultTab: 'overview',
    activityTab: 'analysis-activity',
    isActivityTabVisible,
    shouldShowConfetti: shouldShowFixConfetti(completedFixOutcome),
  })

  const onViewActiveResult = activeResultId ? () => onNavigateToResult?.(activeResultId) : undefined

  const inProgressNotification =
    hasInProgressResult && !isLive ? { status: 'in-progress' as const, text: 'Analysis in progress' } : undefined
  const notification = inProgressNotification ?? hookNotification

  const onNotificationAction = () => {
    switchToActivityTab()
    onViewActiveResult?.()
  }

  useEffect(() => {
    if (finding && isInProgress) {
      switchToActivityTab()
    }
  }, [finding, isInProgress, switchToActivityTab])

  return {
    activityProps,
    isActivityTabVisible,
    selectedTab,
    handleTabChange,
    notification,
    onNotificationAction,
    showConfirmation,
    showConfetti,
    onConfettiComplete,
    analysis,
  }
}

export const classifyFixOutcome = (completedFixResult: FixResultResponse | undefined) => {
  const rawOutcome = completedFixResult?.outcome
  const completedFixOutcome = rawOutcome?.type === 'completed-fix' ? rawOutcome : undefined
  const nonCompletedOutcome =
    rawOutcome?.type === 'failed-fix' ||
    rawOutcome?.type === 'blocked-fix' ||
    rawOutcome?.type === 'excluded-fix' ||
    rawOutcome?.type === 'no-recommendations-fix'
      ? rawOutcome
      : undefined
  // Cancelled fix outcomes are audit-only; downstream consumers treat them as unanalyzed by
  // seeing `outcome === undefined` here.
  const outcome = completedFixOutcome ?? nonCompletedOutcome
  const isInProgress = !completedFixOutcome && !nonCompletedOutcome
  const isLive = !!completedFixResult && !completedFixResult.outcome

  return { outcome, completedFixOutcome, nonCompletedOutcome, isInProgress, isLive }
}

const findRelatedAnalysisResultId = (
  results: FindingResultPaginatedResponse | undefined,
  completedFixResult: FixResultResponse | undefined,
  resultId: string | undefined
): string | undefined => {
  if (!completedFixResult || !resultId) {
    return undefined
  }
  const analysisHref = completedFixResult._links.analysis.href
  const triageResultId = results?._embedded?.items.find(
    r => r.type === 'triage' && r._links.analysis.href === analysisHref && r.outcome?.type === 'completed-triage'
  )?.id
  if (triageResultId) {
    return triageResultId
  }
  return results?._embedded?.items.find(
    r => r.type === 'sca' && r._links.analysis.href === analysisHref && r.outcome?.type === 'completed-sca'
  )?.id
}

const ChangesetsTab: React.FC<Pick<ReturnType<typeof useGetFixResultChangesets>, 'data' | 'isLoading' | 'isError'>> = ({
  data,
  isLoading,
  isError,
}) => {
  if (isLoading) {
    return <div className={typography.paragraph}>Loading changesets...</div>
  }

  if (isError) {
    return <div className={typography.paragraph}>Failed to load changesets.</div>
  }

  const changesets = data?._embedded?.items

  if (!changesets || changesets.length === 0) {
    return <div className={typography.paragraph}>No changesets available for this fix.</div>
  }

  return <ChangesetBlocks changesets={changesets} />
}

const FixArticle: React.FC<{ description?: string | null }> = ({ description }) => {
  if (!description) {
    return <div className={styles.noFixText}>No fix description available for this finding.</div>
  }

  return <RenderedMarkdown markdown={description} />
}

const FixOutcomeDetails: React.FC<{ outcome: NonCompletedFixOutcome }> = ({ outcome }) => {
  return match(outcome)
    .with({ type: 'failed-fix' }, failedOutcome => (
      <p className={styles.descriptionText}>{failedOutcome.problem.message}</p>
    ))
    .with({ type: 'blocked-fix' }, blockedOutcome => (
      <OutcomeReasonDetails
        reason={blockedOutcome.reason}
        details={blockedOutcome.details}
        fallback={
          <p className={styles.descriptionText}>
            A unilateral fix by Pixee is not appropriate without further review and coordination.
          </p>
        }
      />
    ))
    .with({ type: 'excluded-fix' }, excludedOutcome => (
      <p className={styles.descriptionText}>
        {excludedOutcome.reason || 'Automated fixes are configured as out-of-scope for this rule type.'}
      </p>
    ))
    .with({ type: 'no-recommendations-fix' }, () => null)
    .exhaustive()
}

type ConfidenceTabProps = {
  confidence?: Confidence
  quality?: QualityResponse
}

const ConfidenceTab: React.FC<ConfidenceTabProps> = ({ confidence, quality }) => {
  if (!confidence || !quality) {
    return <p className={typography.paragraph}>No confidence or quality details available for this fix.</p>
  }

  return (
    <div className={styles.detailsSections}>
      <div className={styles.detailsSection}>
        <h1 className={classNames(typography.h6, styles.detailsSectionHeading)}>
          <span className={styles.detailsLabel}>Confidence</span> <br />
          {confidenceToText(confidence)}
        </h1>
      </div>
      <div className={styles.detailsSection}>
        <h2 className={classNames(typography.leadBold, styles.detailsSectionHeading)}>
          <span className={styles.detailsLabel}>Safety</span> {quality.safety.score}
        </h2>
        <p className={classNames(typography.paragraph, styles.detailsSectionBody)}>{quality.safety.description}</p>
      </div>
      <div className={styles.detailsSection}>
        <h2 className={classNames(typography.leadBold, styles.detailsSectionHeading)}>
          <span className={styles.detailsLabel}>Effectiveness</span> {quality.effectiveness.score}
        </h2>
        <p className={classNames(typography.paragraph, styles.detailsSectionBody)}>
          {quality.effectiveness.description}
        </p>
      </div>
      <div className={styles.detailsSection}>
        <h2 className={classNames(typography.leadBold, styles.detailsSectionHeading)}>
          <span className={styles.detailsLabel}>Cleanliness</span> {quality.cleanliness.score}
        </h2>
        <p className={classNames(typography.paragraph, styles.detailsSectionBody)}>{quality.cleanliness.description}</p>
      </div>
    </div>
  )
}

type FixPreviewHeaderProps = {
  findingTitle: string | null | undefined
  findingId: string
  scanId: string | undefined
  completedFixOutcome: CompletedFixOutcome | undefined
  nonCompletedOutcome: NonCompletedFixOutcome | undefined
  completedFixResultId: string | undefined
  repositoryType: RepositoryType | undefined
  onSendPatch: () => unknown
  isStale: boolean | undefined
}

const FixPreviewHeader: React.FC<FixPreviewHeaderProps> = ({
  findingTitle,
  findingId,
  scanId,
  completedFixOutcome,
  nonCompletedOutcome,
  completedFixResultId,
  repositoryType,
  onSendPatch,
  isStale,
}) => {
  const isSendPatchLoading = useIsPostScanPatchMutating(scanId)
  const { data: patchesResponse } = useGetFixResultPatches({
    resultId: completedFixResultId,
    pageSize: 1,
  })
  const patch = patchesResponse?._embedded?.items[0]
  const fixSummary = completedFixOutcome?.summary ?? undefined
  const fixId = completedFixOutcome ? completedFixResultId : undefined
  const fixDisplayText =
    nonCompletedOutcome?.type === 'no-recommendations-fix'
      ? 'No fix necessary'
      : nonCompletedOutcome
        ? 'None'
        : undefined
  const isFixPending = !completedFixOutcome && !nonCompletedOutcome
  const fixConfidence = completedFixOutcome?.confidence ?? undefined
  const isStarterFix =
    completedFixOutcome?.quality?.merge_readiness?.score !== undefined &&
    completedFixOutcome.quality.merge_readiness.score < STARTER_FIX_THRESHOLD
  const hideSendPatch = !completedFixOutcome

  return (
    <Dialog.Description className={styles.header} aria-label="Fix preview header" asChild>
      <section>
        <Dialog.Title className={styles.title}>{fixSummary ?? findingTitle}</Dialog.Title>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: '16px' }}>
          <div className={styles.idStack}>
            {(fixId || fixDisplayText || isFixPending) && (
              <div className={styles.fixIdGroup}>
                <label htmlFor="fix-id" className={styles.label}>
                  Fix
                </label>{' '}
                <output id="fix-id" className={styles.fixIdOutput}>
                  {fixId ?? fixDisplayText ?? (isFixPending ? <em>Pending</em> : null)}
                </output>
                {fixId && (
                  <CopyButton copyText={fixId} label="Copy Fix ID to clipboard" className={styles.copyButton} />
                )}
              </div>
            )}
            <div className={styles.fixIdGroup}>
              <label htmlFor="finding-id" className={styles.label}>
                Finding
              </label>{' '}
              <output id="finding-id" className={styles.findingIdOutput}>
                {findingId}
              </output>
              <CopyButton copyText={findingId} label="Copy Finding ID to clipboard" className={styles.copyButton} />
            </div>
          </div>
          {fixConfidence && (
            <div className={styles.confidenceContainer}>
              <div className={styles.fixTagWithTooltip}>
                <label htmlFor="fix-confidence" className={styles.label}>
                  Confidence
                </label>{' '}
                <output id="fix-confidence" className={styles.confidenceOutput}>
                  {confidenceToText(fixConfidence)}
                </output>
                <FixConfidenceTooltip />
              </div>
              {isStarterFix && <StarterFixBadge />}
            </div>
          )}
        </div>
        {!hideSendPatch && (
          <div style={{ marginTop: '16px' }}>
            {match(patch)
              .with({ state: 'in-progress' }, () => (
                <div className={styles.generatingFixContainer}>
                  <span role="status" aria-label="Generating fix" className={styles.generatingFix}>
                    Generating fix
                  </span>
                  <PulsingDot variant="in-progress" className={styles.generatingFixIcon} />
                </div>
              ))
              .with({ state: P.union('completed-success', 'completed-fixes-overcome-by-events') }, patch =>
                match(patch)
                  .with({ type: 'git' }, patch => {
                    return (
                      <div className={styles.successFixContainerMeta}>
                        <div className={styles.successFixContainer}>
                          <span
                            role="status"
                            aria-label={getSuccessPatchStateText(patch.type)}
                            className={styles.successFix}
                          >
                            {getSuccessPatchStateText(patch.type)}
                          </span>
                          <GitBranchIcon className={styles.successFixIcon} />
                        </div>
                        {patch.branch_name && (
                          <div className={styles.successFixBranchNameContainer}>
                            <span className={styles.successFixBranchName}>{patch.branch_name}</span>
                            <CopyButton
                              copyText={patch.branch_name}
                              label="Copy branch name to clipboard"
                              className={styles.copyButton}
                            />
                          </div>
                        )}
                      </div>
                    )
                  })
                  .with({ type: P.union('github', 'gitlab', 'bitbucket', 'azure') }, patch => {
                    return (
                      <div className={styles.successFixContainerMeta}>
                        <div className={styles.successFixContainer}>
                          <span
                            role="status"
                            aria-label={getSuccessPatchStateText(patch.type, patch.merge_state)}
                            className={styles.successFix}
                          >
                            {getSuccessPatchStateText(patch.type, patch.merge_state)}
                          </span>
                          {getSuccessPatchIcon(patch.merge_state)}
                        </div>
                        {patch.pr_number && (
                          <a href={patch.html_url} target="_blank" className={styles.successFixLink}>
                            #{patch.pr_number}
                            <ArrowSquareOutIcon className={styles.successFixLinkIcon} size={12} weight="light" />
                          </a>
                        )}
                      </div>
                    )
                  })
                  .otherwise(() => null)
              )
              .with({ state: 'completed-failed' }, () => (
                <div className={styles.generatingFixContainer}>
                  <DefaultButton
                    buttonType="secondary"
                    state={isSendPatchLoading ? 'loading' : 'default'}
                    loadingLabel={'Fix request in progress...'}
                    onClick={onSendPatch}
                    size="small"
                  >
                    Try again
                  </DefaultButton>
                  <span role="status" aria-label={getFailPatchStateText(patch?.type)} className={styles.failedFix}>
                    {getFailPatchStateText(patch?.type)}
                    <XIcon className={styles.failedFixIcon} size={20} weight="light" />
                  </span>
                </div>
              ))
              .with(P.nullish, () => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <DefaultButton
                    state={isSendPatchLoading ? 'loading' : 'default'}
                    ariaLabel={getActionButtonText(repositoryType)}
                    loadingLabel={'Fix request in progress...'}
                    onClick={onSendPatch}
                    size="small"
                  >
                    {getActionButtonText(repositoryType)}
                  </DefaultButton>
                  {isStale && <span className={styles.staleFixBadge}>Stale fix</span>}
                </div>
              ))
              .exhaustive()}
          </div>
        )}
      </section>
    </Dialog.Description>
  )
}

type NonCompletedOutcomeSummaryProps = {
  outcome: NonCompletedFixOutcome
}

const NonCompletedOutcomeSummary: React.FC<NonCompletedOutcomeSummaryProps> = ({ outcome }) => (
  <div className={styles.tabContainer}>
    <div className={styles.tabList}>
      <div className={styles.tab} data-state="active">
        SUMMARY
      </div>
    </div>
    <div className={styles.tabContent}>
      <OutcomeBanner outcome={outcome} />
      <FixOutcomeDetails outcome={outcome} />
    </div>
  </div>
)

type CompletedFixResultTabsProps = {
  selectedTab: FixFindingDrawerTab
  onTabChange: (value: string) => unknown
  completedFixOutcome: CompletedFixOutcome | undefined
  completedFixResultId: string | undefined
  notification: { status: StatusNotificationStatus; text: string } | undefined
  onNotificationAction: () => unknown
  activityProps: AnalysisActivityTabProps | undefined
  isActivityTabVisible: boolean
  isLive: boolean
  showConfirmation: boolean
  analysisId: string | undefined
  hasTriageResult: boolean
  onViewTriageResult: () => unknown
}

const CompletedFixResultTabs: React.FC<CompletedFixResultTabsProps> = ({
  selectedTab,
  onTabChange,
  completedFixOutcome,
  completedFixResultId,
  notification,
  onNotificationAction,
  activityProps,
  isActivityTabVisible,
  isLive,
  showConfirmation,
  analysisId,
  hasTriageResult,
  onViewTriageResult,
}) => {
  const changesets = useGetFixResultChangesets({ resultId: completedFixResultId })
  const changesetItems = changesets.data?._embedded?.items
  const { data: memory } = useGetAnalysisPreferences({ analysisId })

  return (
    <Tabs.Root value={selectedTab} onValueChange={onTabChange} className={styles.tabContainer}>
      <ScrollableTabBar
        className={styles.tabRow}
        trailingContent={
          hasTriageResult ? (
            <div className={styles.tabRowRight}>
              <GhostButton onClick={onViewTriageResult}>
                <EyeIcon
                  size={16}
                  weight="fill"
                  style={{
                    marginRight: '4px',
                    color: mappedColors.neutral.halo,
                  }}
                />
                <span className={typography.small}>View triage result</span>
              </GhostButton>
            </div>
          ) : undefined
        }
      >
        <Tabs.List className={scrollableTabBarStyles.tabList}>
          <Tabs.Trigger className={styles.tab} value={'overview' satisfies FixFindingDrawerTab}>
            OVERVIEW
          </Tabs.Trigger>
          <Tabs.Trigger className={styles.tab} value={'files-changed' satisfies FixFindingDrawerTab}>
            FILES CHANGED{' '}
            {changesetItems?.length && <span className={styles.changesetCount}>{changesetItems.length}</span>}
          </Tabs.Trigger>
          <Tabs.Trigger className={styles.tab} value={'details' satisfies FixFindingDrawerTab}>
            DETAILS
          </Tabs.Trigger>
          {isActivityTabVisible && (
            <Tabs.Trigger className={styles.tab} value={'analysis-activity' satisfies FixFindingDrawerTab}>
              ANALYSIS ACTIVITY
            </Tabs.Trigger>
          )}
          {memory && (
            <Tabs.Trigger className={styles.tab} value={'preferences' satisfies FixFindingDrawerTab}>
              PREFERENCES
            </Tabs.Trigger>
          )}
        </Tabs.List>
      </ScrollableTabBar>

      {notification && (
        <div className={styles.notificationWrapper}>
          <StatusNotification
            status={notification.status}
            text={notification.text}
            actionLabel="View"
            onAction={onNotificationAction}
          />
        </div>
      )}

      <Tabs.Content className={styles.tabContent} value={'overview' satisfies FixFindingDrawerTab}>
        <FixArticle description={completedFixOutcome?.description} />
      </Tabs.Content>

      {/* forceMount keeps the DiffView DOM alive when switching tabs, preserving
          its internal syntax highlighting state which is lost on unmount/remount. */}
      <Tabs.Content
        forceMount
        className={styles.forceMountedTabContent}
        value={'files-changed' satisfies FixFindingDrawerTab}
      >
        <ChangesetsTab data={changesets.data} isLoading={changesets.isLoading} isError={changesets.isError} />
      </Tabs.Content>

      <Tabs.Content className={styles.tabContent} value={'details' satisfies FixFindingDrawerTab}>
        <ConfidenceTab
          confidence={completedFixOutcome?.confidence ?? undefined}
          quality={completedFixOutcome?.quality ?? undefined}
        />
      </Tabs.Content>

      {activityProps && (
        <Tabs.Content className={styles.tabContent} value={'analysis-activity' satisfies FixFindingDrawerTab}>
          <AnalysisActivityTab
            {...activityProps}
            progressLabel="Fix analysis"
            isLive={isLive}
            overlay={showConfirmation ? <AnalysisTransitionOverlay message="Rendering fix result" /> : undefined}
          />
        </Tabs.Content>
      )}

      {memory && (
        <Tabs.Content className={styles.tabContent} value={'preferences' satisfies FixFindingDrawerTab}>
          <AnalysisPreferencesTab memory={memory} />
        </Tabs.Content>
      )}
    </Tabs.Root>
  )
}

type InProgressActivityViewProps = {
  hasTriageResult: boolean
  onBackToTriageResult: () => unknown
  activityProps: AnalysisActivityTabProps | undefined
  isLive: boolean
  showConfirmation: boolean
}

const InProgressActivityView: React.FC<InProgressActivityViewProps> = ({
  hasTriageResult,
  onBackToTriageResult,
  activityProps,
  isLive,
  showConfirmation,
}) => {
  return (
    <div className={styles.tabContainer}>
      <div>
        {hasTriageResult && (
          <GhostButton onClick={onBackToTriageResult}>
            <ArrowLeftIcon
              style={{
                marginRight: '4px',
                color: mappedColors.neutral.halo,
                width: '16px',
                height: '16px',
              }}
            />
            <span className={typography.small}>Back to triage result</span>
          </GhostButton>
        )}
        {activityProps && (
          <AnalysisActivityTab
            {...activityProps}
            progressLabel="Fix analysis"
            isLive={isLive}
            overlay={showConfirmation ? <AnalysisTransitionOverlay message="Rendering fix result" /> : undefined}
          />
        )}
      </div>
    </div>
  )
}

const FixConfidenceTooltip: React.FC = () => {
  return (
    <Tooltip
      label="See details about fix confidence"
      width={250}
      side="bottom"
      sideOffset={10}
      contentClassName={styles.tooltipContent}
    >
      <>
        <p
          className={typography.extraSmallBold}
          style={{ textTransform: 'uppercase', color: mappedColors.neutral.neutral30, marginBottom: '16px' }}
        >
          Beta Feature
        </p>
        <p style={{ marginBottom: '0' }}>
          <b>Fix confidence</b> is a rating derived from an internal benchmark and includes High, Medium, and Low
          confidence fixes. It is comprised of three weighted scores reflecting the safety, effectiveness and
          cleanliness of Pixee's code changes within a fix. <b>See Details tab for more information.</b>
        </p>
      </>
    </Tooltip>
  )
}

const StarterFixBadge: React.FC = () => {
  return (
    <TooltipWithHover
      trigger={<span className={styles.starterFixBadge}>Starter fix</span>}
      side="bottom"
      sideOffset={6}
      maxWidth={280}
    >
      <b>Starter fixes</b> implement the correct fix strategy but may require further developer intervention to add or
      edit controls that are specific to your application and organization
    </TooltipWithHover>
  )
}

const getActionButtonText = (repositoryType?: RepositoryType, count: number = 1): string => {
  return match(repositoryType)
    .with('gitlab', () => `Create ${pluralize(count, 'merge request', 'merge requests')}`)
    .with('github', () => `Create ${pluralize(count, 'pull request', 'pull requests')}`)
    .with('azure', () => `Create ${pluralize(count, 'pull request', 'pull requests')}`)
    .with('bitbucket', () => `Create ${pluralize(count, 'pull request', 'pull requests')}`)
    .with('git', () => `Push to ${pluralize(count, 'branch', 'branches')}`)
    .with(undefined, () => `Push to ${pluralize(count, 'branch', 'branches')}`)
    .exhaustive()
}

const getSuccessPatchStateText = (repositoryType?: RepositoryType, mergeState?: MergeStateResponse): string => {
  return match(repositoryType)
    .with('gitlab', () => getPullRequestStateText('MR', mergeState))
    .with('github', () => getPullRequestStateText('PR', mergeState))
    .with('azure', () => getPullRequestStateText('PR', mergeState))
    .with('bitbucket', () => getPullRequestStateText('PR', mergeState))
    .with('git', () => 'Pushed to branch')
    .with(undefined, () => 'Pushed to branch')
    .exhaustive()
}

const getPullRequestStateText = (noun: 'PR' | 'MR', mergeState?: MergeStateResponse): string => {
  return match(mergeState)
    .with('closed_merged', () => `${noun} merged`)
    .with('closed_not_merged', () => `${noun} closed`)
    .with('open', undefined, () => `${noun} created`)
    .exhaustive()
}

const getSuccessPatchIcon = (mergeState?: MergeStateResponse): React.ReactNode => {
  return match(mergeState)
    .with('closed_merged', () => <GitMergeIcon className={styles.successFixIconMerged} />)
    .with('closed_not_merged', () => <XIcon className={styles.successFixIconClosed} size={16} weight="bold" />)
    .otherwise(() => <GitBranchIcon className={styles.successFixIcon} />)
}

const getFailPatchStateText = (repositoryType?: RepositoryType): string => {
  return match(repositoryType)
    .with('gitlab', () => 'MR creation failed')
    .with('github', () => 'PR creation failed')
    .with('azure', () => 'PR creation failed')
    .with('bitbucket', () => 'PR creation failed')
    .with('git', () => 'Push to branch failed')
    .with(undefined, () => 'Push to branch failed')
    .exhaustive()
}

const ChangesetBlocks = ({ changesets }: { changesets: Changeset[] }) => {
  const getValue = (changeset: Changeset) => `${changeset.path}${changeset.preview.length}`

  return (
    <Accordion.Root type="multiple" defaultValue={changesets.map(getValue)} className={styles.accordion}>
      {changesets.map(changeset => (
        <Accordion.Item key={getValue(changeset)} value={getValue(changeset)} className={styles.accordionItem}>
          <Accordion.Header className={styles.accordionHeader}>
            <Accordion.Trigger className={styles.accordionTrigger}>
              <CaretDownIcon className={styles.caretDown} size={20} weight="light" />
              <CaretUpIcon className={styles.caretUp} size={20} weight="light" />
              {changeset.path}
            </Accordion.Trigger>
          </Accordion.Header>
          {/* forceMount keeps the DiffView in the DOM when the accordion collapses,
              preserving syntax highlighting state. Hidden via CSS data-state selector. */}
          <Accordion.Content forceMount className={styles.accordionContent}>
            <ChangesetDiffView preview={changeset.preview} filePath={changeset.path} />
          </Accordion.Content>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  )
}

const confidenceToText = (confidence: Confidence): string =>
  match(confidence)
    .with('low', () => 'Low')
    .with('medium', () => 'Medium')
    .with('high', () => 'High')
    .with('no-rating', () => 'No rating')
    .exhaustive()

type StaleFixConfirmationDialogPureProps = {
  currentScanImportedAt?: string | null
  newerScanImportedAt?: string | null
  repositoryType?: RepositoryType
  onCancel: () => unknown
  onConfirm: () => unknown
}

const StaleFixConfirmationDialogPure: React.FC<StaleFixConfirmationDialogPureProps> = ({
  currentScanImportedAt,
  newerScanImportedAt,
  repositoryType,
  onCancel,
  onConfirm,
}) => (
  <Dialog.Root open={true}>
    <Dialog.Portal>
      <Dialog.Overlay className={alertDialogStyles.alertDialogOverlay} />
      <Dialog.Content
        className={alertDialogStyles.alertDialogContent}
        onOpenAutoFocus={e => e.preventDefault()}
        onEscapeKeyDown={onCancel}
        onPointerDownOutside={onCancel}
      >
        <Dialog.Title className={alertDialogStyles.alertDialogTitle} style={{ textAlign: 'left' }}>
          <WarningIcon className={alertDialogStyles.alertDialogIcon} size={32} weight="fill" />
          Continue?
        </Dialog.Title>
        <Dialog.Description className={alertDialogStyles.alertDialogDescription} style={{ textAlign: 'left' }} asChild>
          <div>
            <p className={styles.staleDialogText}>
              You are about to initiate code changes in response to a finding from a <strong>stale</strong> scan. A
              newer scan exists that may contain more accurate analysis.
            </p>
            <div className={styles.staleComparisonBox}>
              <div className={styles.staleComparisonRow}>
                <div className={styles.staleComparisonColumn}>
                  <span className={styles.staleComparisonLabel}>This scan</span>
                  {currentScanImportedAt && (
                    <>
                      <span className={styles.staleComparisonDateVariant({ variant: 'stale' })}>
                        {DateTime.fromISO(currentScanImportedAt).toFormat('M/d/yy')}
                      </span>
                      <span className={styles.staleComparisonTimeVariant({ variant: 'stale' })}>
                        {DateTime.fromISO(currentScanImportedAt).toFormat('hh:mm:ss a ZZZZ')}
                      </span>
                    </>
                  )}
                  <ScanStatusBadge variant="stale" />
                </div>
                <ArrowRightIcon className={styles.staleComparisonArrow} size={24} weight="light" />
                <div className={styles.staleComparisonColumn}>
                  <span className={styles.staleComparisonLabel}>Newer scan</span>
                  {newerScanImportedAt && (
                    <>
                      <span className={styles.staleComparisonDateVariant({ variant: 'latest' })}>
                        {DateTime.fromISO(newerScanImportedAt).toFormat('M/d/yy')}
                      </span>
                      <span className={styles.staleComparisonTimeVariant({ variant: 'latest' })}>
                        {DateTime.fromISO(newerScanImportedAt).toFormat('hh:mm:ss a ZZZZ')}
                      </span>
                    </>
                  )}
                  <ScanStatusBadge variant="latest" />
                </div>
              </div>
            </div>
            <p className={styles.staleDialogText}>
              Acting on stale analysis may result in redundancy. It is recommended to review the latest scan first. Do
              you still wish to proceed?
            </p>
          </div>
        </Dialog.Description>
        <div className={alertDialogStyles.alertDialogButtons}>
          <GhostButton type="secondary" onClick={onCancel}>
            Cancel
          </GhostButton>
          <DefaultButton buttonType="secondary" onClick={onConfirm}>
            {getActionButtonText(repositoryType)}
          </DefaultButton>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
)
